import React, { useState, useEffect } from "react";
import { Box, Typography, InputAdornment, TextField, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";
import { selectMultiViewEvent, cancelSelectingSlot, IMultiViewSlot } from "../../store/player/playerSlice";
import { useLazyGetContentDetailsQuery, useLazyGetNearbyEventsQuery } from "../../api/modules";
import helper from "../../constants/helper";
import { SPORTS_LIVE_NOW_STATUS } from "../../constants";
import { ProgramPages } from "../../types";
import { IRelated } from "../Cards/types";
import { Loader } from "../index";
import styles from "./VideoPlayer.module.css";

interface EventSelectorProps {
    slotIndex: number;
}

const EventSelector: React.FC<EventSelectorProps> = ({ slotIndex }) => {
    const { t } = useTranslation("translations");
    const dispatch = useDispatch();
    const role = localStorage.getItem(helper.EPIC_PROFILE_ID) ? localStorage.getItem(helper.EPIC_USER_ROLE) : null;
    const profileID = localStorage.getItem(helper.EPIC_PROFILE_ID);
    const { contentContext, multiViewSlots } = useSelector((state: RootState) => state.videoPlayer);
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingId, setLoadingId] = useState<number | null>(null);

    const [getContentDetails] = useLazyGetContentDetailsQuery();
    const [getNearbyEvents] = useLazyGetNearbyEventsQuery();

    const pageName = contentContext?.page ?? ProgramPages.SPORTS;

    const [sportsItems, setSportsItems] = useState<IRelated[]>([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const selectedEventIds = new Set(
        multiViewSlots
            .map((slot) => Number(slot.id))
            .filter(Boolean)
    );
    const availableSportsItems = sportsItems.filter((item) => !selectedEventIds.has(Number(item.id)));

    useEffect(() => {
        let isMounted = true;
        const search = searchQuery.trim();
        const timeoutId = window.setTimeout(() => {
            const loadSportsPrograms = async () => {
                setIsLoadingItems(true);

                try {
                    let page = 1;
                    let hasNextPage = true;
                    const itemsMap = new Map<number, IRelated>();

                    while (hasNextPage && isMounted) {
                        const response = await getNearbyEvents({
                            page_number: page,
                            rehydrated_pagination: true,
                            role,
                            status: SPORTS_LIVE_NOW_STATUS,
                            ...(search ? { search } : {}),
                        }).unwrap();

                        (response?.results ?? []).forEach((item: IRelated) => {
                            if (item.id && !itemsMap.has(Number(item.id))) {
                                itemsMap.set(Number(item.id), item);
                            }
                        });

                        hasNextPage = Boolean(response?.next);
                        page += 1;
                    }

                    if (!isMounted) return;

                    setSportsItems(Array.from(itemsMap.values()));
                } catch {
                } finally {
                    if (isMounted) setIsLoadingItems(false);
                }
            };

            void loadSportsPrograms();
        }, 300);

        return () => {
            isMounted = false;
            window.clearTimeout(timeoutId);
        };
    }, [searchQuery, role, getNearbyEvents]);

    const handleSelectEvent = async (item: IRelated) => {
        const itemId = Number(item.id);
        if (!itemId) return;
        setLoadingId(itemId);

        try {
            let videoUrl = item.video?.url;
            let subtitle = item.video?.subtitle ?? "";

            if (!videoUrl) {
                const response = await getContentDetails({
                    id: itemId,
                    role,
                    profile_id: profileID ? Number(profileID) : undefined,
                }).unwrap();
                if (response?.video?.url) {
                    videoUrl = response.video.url;
                    subtitle = response.video.subtitle ?? "";
                }
            }

            const slotData: IMultiViewSlot = {
                id: itemId,
                title: item.title || t("Sports.event"),
                poster_path: item.poster_path || item.backdrop_path || "",
                backdrop_path: item.backdrop_path || item.poster_path || "",
                type: item.type || pageName,
                videoData: {
                    id: itemId,
                    url: videoUrl || "",
                    subtitle: subtitle,
                },
                isMuted: slotIndex > 0,
                isPlaying: true,
                isSelectingEvent: false,
            };

            dispatch(selectMultiViewEvent({ index: slotIndex, slot: slotData }));
        } catch (err) {
            console.error("Error selecting event for slot:", err);
        } finally {
            setLoadingId(null);
        }
    };

    const handleCancel = () => {
        dispatch(cancelSelectingSlot(slotIndex));
    };

    return (
        <Box className={styles.eventSelectorContainer}>
            <Box className={styles.eventSelectorHeader}>
                <Typography variant="h6" className={styles.eventSelectorTitle}>
                    {t("Sports.selectEvent") || "Select an Event"}
                </Typography>
                <IconButton className={styles.eventSelectorCloseBtn} onClick={handleCancel}>
                    <CloseIcon sx={{ color: "#fff" }} />
                </IconButton>
            </Box>

            <Box className={styles.eventSelectorSearchBox}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("Sports.searchPlaceholder") || "Search sports..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "rgba(255,255,255,0.5)" }} />
                            </InputAdornment>
                        ),
                        sx: {
                            color: "#fff",
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            borderRadius: "12px",
                            "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                            "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                        },
                    }}
                />
            </Box>

            <Box className={styles.eventSelectorList}>
                {isLoadingItems && (
                    <Box className={styles.eventSelectorLoader}>
                        <Loader />
                    </Box>
                )}

                {!isLoadingItems && availableSportsItems.length === 0 && (
                    <Typography className={styles.eventSelectorEmpty}>
                        {t("Sports.noEventsFound", { defaultValue: "No events found" })}
                    </Typography>
                )}

                {!isLoadingItems &&
                    availableSportsItems.map((item) => (
                        <Box
                            key={item.id}
                            className={styles.eventSelectorCard}
                            onClick={() => handleSelectEvent(item)}
                        >
                            <Box className={styles.eventSelectorCardPoster}>
                                <img src={item.backdrop_path || item.poster_path} alt={item.title} />
                                <span className={styles.eventSelectorLiveBadge}>● LIVE</span>
                            </Box>
                            <Box className={styles.eventSelectorCardInfo}>
                                <Typography variant="subtitle1" className={styles.eventSelectorCardTitle}>
                                    {item.title}
                                </Typography>
                                {item.synopsis && (
                                    <Typography variant="caption" className={styles.eventSelectorCardSubtitle}>
                                        {item.synopsis}
                                    </Typography>
                                )}
                            </Box>
                            {loadingId === Number(item.id) && (
                                <Box className={styles.eventSelectorCardOverlay}>
                                    <Loader />
                                </Box>
                            )}
                        </Box>
                    ))}
            </Box>
        </Box>
    );
};

export default EventSelector;
